import * as Sentry from "@sentry/react";
import type { ErrorEvent, Event } from "@sentry/react";

const SENSITIVE_KEYS = /^(authorization|cookie|set-cookie|access_?token|refresh_?token|code|password|secret|api_?key|ssn|ein|account|routing|message|body|document|filename|email|phone|name|address)$/i;

export function initializeBrowserObservability() {
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim();
  Sentry.init({
    dsn,
    enabled: Boolean(dsn),
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE?.trim() || undefined,
    sendDefaultPii: false,
    tracesSampleRate: boundedSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.05),
    beforeSend: (event) => scrubBrowserEvent(event) as ErrorEvent | null,
  });
}

export function scrubBrowserEvent(event: Event): Event | null {
  const clean = deepScrub(structuredClone(event)) as Event;
  delete clean.user;
  delete clean.breadcrumbs;
  if (clean.request) {
    delete clean.request.data;
    delete clean.request.cookies;
    delete clean.request.headers;
  }
  return clean;
}

function deepScrub(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(deepScrub);
  if (typeof value === "string") return value
    .replace(/\b\d{3}-\d{2}-\d{4}\b|\b\d{9}\b/g, "[tax-id-redacted]")
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[email-redacted]")
    .replace(/\bBearer\s+[A-Za-z0-9._~+\/-]+/gi, "Bearer [redacted]");
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, SENSITIVE_KEYS.test(key) ? "[redacted]" : deepScrub(item)]));
}

function boundedSampleRate(value: string | undefined, fallback: number) {
  if (!value?.trim()) return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 0.05) {
    throw new Error(`VITE_SENTRY_TRACES_SAMPLE_RATE must be between 0 and 0.05, received: "${value}".`);
  }
  return parsed;
}
